import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdAttachFile } from "react-icons/md";

import { checkFileType, convertTime } from "../../../../../../helpers/helpers";
import {
  createOrderNotes,
  fetchOrderNotes,
} from "../../../../../../services/orders.services";
import noImage from "../../../../../../assets/images/noImage.png";
import pdfIcon from "../../../../../../assets/icons/pdfIcon.png";
import excelIcon from "../../../../../../assets/icons/excelIcon.png";
import docsIcon from "../../../../../../assets/icons/docsIcon.png";
import ImagesModal from "../../../../../Shipper/Customer/MainPages/Orders/Components/OrderHistoryModal/ImagesModal/ImagesModal";
import CircularLoader from "../../../../../../Components/CircularLoader/CircularLoader";
import {
  isVideoAttachment,
  VideoTile,
} from "../../../../../../Components/AttachmentVideo/AttachmentVideo";
import OrderTracking from "./OrderTracking";

const initials = (name) =>
  String(name ?? "")
    .trim()
    .slice(0, 2) || "?";

const attachmentThumb = (file) => {
  const kind = checkFileType(file);
  if (kind === "Image") return file;
  if (kind === "PDF") return pdfIcon;
  if (kind === "Excel") return excelIcon;
  if (kind === "Document") return docsIcon;
  return noImage;
};

// Renders inline parcel references (e.g. "P4") as chips. Note text has no
// structured link to a parcel record, so this is a plain text-pattern match —
// not tied to the parcel's actual colour/lane.
const renderNoteBody = (text) => {
  const parts = String(text ?? "-").split(/(\bP\d{1,3}\b)/g);
  return parts.map((part, index) =>
    /^P\d{1,3}$/.test(part) ? (
      <span
        key={index}
        className="rounded border border-ui-border bg-ui-surface-sunken px-1.5 py-0.5 font-mono text-[11px] font-semibold text-ui-text-muted"
      >
        {part}
      </span>
    ) : (
      part
    ),
  );
};

// Tracking (parcels first, see OrderTracking) over the comments.
// `trackedParcel` / `onTrackParcel`: the parcel open in Tracking, the view's
// so a Parcels row can open one; `trackingRef` lets the view scroll to it.
export default function OrderActivity({
  data,
  setSnack,
  trackedParcel = null,
  onTrackParcel = () => {},
  trackingRef,
}) {
  const { t } = useTranslation("warehouse");
  const [notes, setNotes] = useState([]);
  const [draft, setDraft] = useState("");
  const [files, setFiles] = useState([]);
  const [posting, setPosting] = useState(false);
  const [fullview, setFullview] = useState();

  useEffect(() => {
    setNotes(data?.order_notes || []);
  }, [data?.order_notes]);

  const handlePost = async () => {
    const note = draft.trim();
    if ((!note && !files.length) || posting) return;

    setPosting(true);
    try {
      const formData = new FormData();
      formData.append("note", note);
      if (files.length) {
        files.forEach((file, index) => {
          formData.append(`attachments[${index}]`, file, file.name);
        });
      } else {
        formData.append("attachments", []);
      }

      const response = await createOrderNotes(data?.id, formData);

      if (response?.error) {
        setSnack?.({
          open: true,
          text:
            response?.error?.response?.data?.message ||
            t("orderView.couldNotAddTheNote"),
          severity: "error",
        });
        return;
      }

      setDraft("");
      setFiles([]);
      setSnack?.({ open: true, text: t("orderView.commentAdded"), severity: "success" });

      const refreshed = await fetchOrderNotes(data?.id);
      if (!refreshed?.error && refreshed?.data?.result) {
        setNotes(refreshed.data.result);
      }
    } catch (error) {
      setSnack?.({
        open: true,
        text: t("orderView.somethingWentWrongPleaseTryAgain"),
        severity: "error",
      });
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="flex min-h-0 flex-col gap-2">
      <ImagesModal setOpen={setFullview} open={fullview} />

      <OrderTracking
        key={data?.id}
        data={data}
        selected={trackedParcel}
        onSelect={onTrackParcel}
        panelRef={trackingRef}
      />

      <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg max-lg:max-h-[340px] max-lg:flex-none border border-ui-border bg-ui-surface">
        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-ui-border px-3 py-2">
          <h3 className="m-0 text-[13.5px] font-semibold text-ui-text">
            {t("orderView.comments")}
            {notes.length > 0 && (
              <span className="ml-1.5 rounded bg-state-neutral-bg px-1.5 py-0.5 text-[10.5px] font-medium text-state-neutral-text">
                {notes.length}
              </span>
            )}
          </h3>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto thin__scrollbar">
          {notes.length ? (
            notes.map((note, index) => (
              <article
                key={note?.id ?? index}
                className="flex gap-2.5 border-b border-ui-border-subtle px-3 py-2.5 last:border-b-0"
              >
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-light text-[10px] font-semibold uppercase text-brand-text">
                  {initials(note?.created_by?.username)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="truncate text-[12px] font-semibold text-ui-text">
                      {note?.created_by?.username ?? "-"}
                    </span>
                    <span className="shrink-0 text-[11px] text-ui-text-subtle lg:text-[10.5px]">
                      {convertTime(note?.created_at)}
                    </span>
                  </div>
                  <p className="m-0 mt-0.5 whitespace-pre-wrap break-words text-[12px] text-ui-text-muted">
                    {renderNoteBody(note?.note)}
                  </p>

                  {note?.order_attachments?.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {note.order_attachments.map((attachment, fileIndex) =>
                        isVideoAttachment(attachment) ? (
                          <VideoTile
                            key={attachment?.id ?? fileIndex}
                            src={attachment?.file}
                            label={`Video ${fileIndex + 1}`}
                            className="h-11 w-11 lg:h-9 lg:w-9"
                          />
                        ) : (
                          <img
                            key={attachment?.id ?? fileIndex}
                            src={attachmentThumb(attachment?.file)}
                            alt={t("orderView.attachmentNumber", { number: fileIndex + 1 })}
                            loading="lazy"
                            onError={(event) => {
                              event.target.src = noImage;
                            }}
                            onClick={() =>
                              checkFileType(attachment?.file) === "Image"
                                ? setFullview(attachment?.file || "")
                                : window.open(attachment?.file, "_blank")
                            }
                            className="h-11 w-11 cursor-pointer rounded border border-ui-border object-cover lg:h-9 lg:w-9"
                          />
                        ),
                      )}
                    </div>
                  )}
                </div>
              </article>
            ))
          ) : (
            <p className="m-0 py-6 text-center text-[12px] text-ui-text-subtle">
              {t("orderView.noCommentsYet")}
            </p>
          )}
        </div>

        {/* Phones: a 16px comment box (typing in it doesn't zoom the page on
            iPhones) and 40px Attach / Post. */}
        <div className="shrink-0 p-2">
          <div className="rounded-lg border border-ui-border bg-ui-surface focus-within:border-brand">
            <textarea
              rows={1}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder={t("orderView.addAComment")}
              className="block max-h-24 w-full resize-none rounded-t-lg bg-transparent px-3 pb-1 pt-2 text-[16px] leading-[1.45] text-ui-text placeholder-ui-text-subtle outline-none sm:text-[12px]"
            />
            <div className="flex items-center gap-2 px-2 pb-1.5">
              <label className="flex min-h-[40px] cursor-pointer items-center gap-1.5 text-[12px] text-ui-text-subtle transition-colors hover:text-brand-text lg:min-h-0 lg:text-[11.5px] [@media(pointer:coarse)]:min-h-[40px]">
                <MdAttachFile size={14} />
                {files.length > 0
                  ? t("orderView.fileCount", { count: files.length })
                  : t("orderView.attach")}
                <input
                  type="file"
                  multiple
                  hidden
                  onChange={(event) =>
                    setFiles(Array.from(event.target.files || []))
                  }
                />
              </label>

              <button
                type="button"
                onClick={handlePost}
                disabled={(!draft.trim() && !files.length) || posting}
                className="ml-auto flex min-h-[40px] items-center gap-1.5 rounded-md bg-brand px-3.5 py-1.5 text-[12px] font-semibold text-brand-contrast transition-colors hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50 lg:min-h-0 lg:text-[11.5px] [@media(pointer:coarse)]:min-h-[40px]"
              >
                {posting && <CircularLoader size={12} />}
                {t("orderView.post")}
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
